// Globals
import { useTranslation } from 'react-i18next';

// Modules
import { FilesListUploaderContainer, UploadCard } from '@/modules/Upload';

// Components
import { IFormInstance } from '@/components';
import { Button, Spinner } from '@/ui';
import RateAndConditionReportForm from '../../components/RateAndConditionReportForm/RateAndConditionReportForm';
import ChangeOrderStatus from '../ChangeOrderStatus/ChangeOrderStatus';
import OrderDetails from '../OrderDetails/OrderDetails';
import FloorPlan from '../FloorPlan/FloorPlan';
import { StyledContent, StyledLink, StyledWrapper, StyledSecondaryTitle } from './AppraiserDetailsStyles';

// Models
import { FileVariant, IFile } from '@/models/file.model';
import { IOrder, OrderStatus } from '@/models/order.model';
import { ROUTES } from '@/models/router.model';

// Helpers
import { getEntityId } from '@/utils';
import { ACCEPT_APPRAISER_FILE_TYPES, MAX_APPRAISER_FILE_SIZE, TUploadConditionReportFields } from '../../helpers';
import { normalizeAppraiserData } from '../../helpers/normalizers';

type TAppraiserDetailsProps = {
  order: IOrder;
  isLoading?: boolean;
  onChangeStatus?: (status: OrderStatus) => void;
  formInstance?: IFormInstance;
  onFileUpload?: (files: File[]) => void;
  onSubmit?: (values: TUploadConditionReportFields) => void;
  onValuesChange?: (values: TUploadConditionReportFields) => void;
  handleUploadPhoto: (filesList: File[], type: FileVariant) => void;
  onPreview?: (file: IFile) => void;
  onDelete: (file: IFile) => void;
};

const AppraiserDetails = ({
  order,
  onChangeStatus,
  isLoading,
  onFileUpload,
  onDelete,
  onPreview,
  onSubmit,
  onValuesChange,
  handleUploadPhoto,
  formInstance,
}: TAppraiserDetailsProps) => {
  const { t } = useTranslation('order');
  const handleUpload = (_: File, filesList: File[]) => onFileUpload?.(filesList);

  return (
    <Spinner spinning={isLoading}>
      <StyledContent>
        <OrderDetails order={order} />
        <ChangeOrderStatus value={order.status} loading={isLoading} onChange={onChangeStatus} />
        <StyledLink
          path={{
            pathname: ROUTES.PROPERTIES,
            search: new URLSearchParams({
              id: getEntityId(order.property_id),
              tab: 'property_tabs_progress',
            }).toString(),
          }}
        >
          {t('appraiser_files_progress')}
        </StyledLink>
        {order.status === OrderStatus.IN_PROGRESS && (
          <>
            <StyledSecondaryTitle>{t('appraiser_condition_report_title')}</StyledSecondaryTitle>

            <StyledWrapper>
              <FilesListUploaderContainer
                onChange={handleUpload}
                onPreview={onPreview}
                onDelete={onDelete}
                acceptance={ACCEPT_APPRAISER_FILE_TYPES}
                maxFileSize={MAX_APPRAISER_FILE_SIZE}
                fileList={order?.appraisal?.files ?? []}
                description={t('upload_report')}
                multiple
              >
                <Button type="primary" shape="round">
                  {t('upload', {
                    ns: 'common',
                    count: order?.appraisal?.files?.length ?? 0,
                  })}
                </Button>
              </FilesListUploaderContainer>
              <RateAndConditionReportForm
                onSubmit={onSubmit}
                formInstance={formInstance}
                initialValues={normalizeAppraiserData(order?.appraisal)}
                onValuesChange={onValuesChange}
              />
            </StyledWrapper>
          </>
        )}
        {order.status === OrderStatus.COMPLETED && (
          <StyledWrapper>
            {order?.appraisal?.files?.map((file) => (
              <UploadCard key={file.id} file={file} onPreview={onPreview} />
            ))}
          </StyledWrapper>
        )}

        {(order.status === OrderStatus.IN_PROGRESS || !!order?.appraisal?.floor_plan?.length) && (
          <>
            <StyledSecondaryTitle>{t('appraiser_floor_plan_title')}</StyledSecondaryTitle>

            <FloorPlan order={order} onDelete={onDelete} onUploadFile={handleUploadPhoto} />
          </>
        )}
      </StyledContent>
    </Spinner>
  );
};

export default AppraiserDetails;
